import { useState, useRef, useCallback } from 'react';

interface SparklineProps {
  data: number[];
  labels?: string[];
  color?: string;
  height?: number;
  className?: string;
  interactive?: boolean;
  valuePrefix?: string;
}

/** Attempt a smooth cubic-bezier path through points; falls back to polyline. */
function smoothPath(pts: { x: number; y: number }[]): string {
  if (pts.length < 3) return 'M' + pts.map(p => `${p.x},${p.y}`).join(' L');

  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(i - 1, 0)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(i + 2, pts.length - 1)];
    const tension = 0.3;
    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;
    d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
}

export function Sparkline({
  data,
  labels,
  color = 'rgba(255,255,255,0.3)',
  height = 24,
  className = '',
  interactive = false,
  valuePrefix = '',
}: SparklineProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  if (data.length < 2) return null;

  const width = 200;
  const yPad = 4;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const pointCoords = data.map((value, i) => ({
    x: (i / (data.length - 1)) * width,
    y: yPad + ((max - value) / range) * (height - yPad * 2),
    value,
  }));

  const curvePath = smoothPath(pointCoords);
  const areaPath = `${curvePath} L${width},${height} L0,${height} Z`;
  const gradientId = `sp-${color.replace(/[^a-zA-Z0-9]/g, '')}-${height}`;

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!interactive || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const idx = Math.round(relX * (data.length - 1));
    setHoverIndex(Math.max(0, Math.min(data.length - 1, idx)));
  }, [interactive, data.length]);

  const handleMouseLeave = useCallback(() => setHoverIndex(null), []);

  const hp = hoverIndex !== null ? pointCoords[hoverIndex] : null;

  // Keep tooltip from clipping edges
  const tooltipLeft = hp ? Math.max(8, Math.min(92, (hp.x / width) * 100)) : 0;

  return (
    <div className={`relative ${className}`} style={{ height: interactive ? height + 6 : height }}>
      <svg
        ref={svgRef}
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.12} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>

        {/* Fill */}
        <path d={areaPath} fill={`url(#${gradientId})`} />

        {/* Line */}
        <path
          d={curvePath}
          fill="none"
          stroke={color}
          strokeWidth={1}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={hoverIndex !== null ? 0.5 : 0.8}
        />

        {/* Hover elements */}
        {interactive && hp && (
          <>
            {/* Thin vertical guide */}
            <line
              x1={hp.x} y1={0} x2={hp.x} y2={height}
              stroke="rgba(255,255,255,0.08)"
              strokeWidth={0.5}
            />
            {/* Glow behind dot */}
            <circle cx={hp.x} cy={hp.y} r={6} fill={color} opacity={0.15} />
            {/* Crisp dot */}
            <circle cx={hp.x} cy={hp.y} r={2} fill="white" opacity={0.9} />
          </>
        )}
      </svg>

      {/* Tooltip */}
      {interactive && hp && hoverIndex !== null && (
        <div
          className="absolute pointer-events-none z-20"
          style={{
            bottom: height + 2,
            left: `${tooltipLeft}%`,
            transform: 'translateX(-50%)',
          }}
        >
          <div
            className="flex items-baseline gap-1.5 px-1.5 py-0.5 rounded-md backdrop-blur-sm"
            style={{
              background: 'rgba(0,0,0,0.65)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <span className="text-[0.65rem] font-medium text-white/90 tabular-nums">
              {valuePrefix}{Math.round(hp.value).toLocaleString()}
            </span>
            {labels?.[hoverIndex] && (
              <span className="text-[0.55rem] text-white/40">
                {labels[hoverIndex]}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
